/**
 * programWorkstationClient — the page's side of a workspace's Windows workstation
 * (docs/PLAN-windows-workstation-20261009.md, W2).
 *
 * Three conversations, each with its own owner:
 *   - the platform (prest0nProgramWorkstation): the workstation's state, waking it, setting it up, and the
 *     person's own "Done";
 *   - the platform's ticket minter (prest0nWorkstationTicket, with `workstation`): a ticket for THIS machine,
 *     which reaches only the person's own pass on it;
 *   - the workstation's agent (`${baseUrl}/browser-api/api/desktop`): a pass onto its one screen, kept alive while
 *     the window is open and ended when it closes.
 * The live view's address is the Linux workstation's recipe (buildDesktopNoVncUrl), so the window draws it the way
 * the sign-in window does. Nothing here logs or keeps a ticket beyond the window that holds it.
 *
 * Firebase is loaded when a call is made, never at import: the Legal apps rows and the chat's card import this to
 * ask the workstation's state, and nothing they render may reach Firebase as it loads.
 */

export const PROGRAM_WORKSTATION_CALLABLE = 'prest0nProgramWorkstation';
export const WORKSTATION_TICKET_CALLABLE = 'prest0nWorkstationTicket';

/** The workstation's states (the server's WORKSTATION_STATES, programWorkstationCore.ts; pinned equal by a test). */
export const WORKSTATION_STATES = ['off', 'none', 'provisioning', 'starting', 'ready', 'asleep', 'stopping', 'failed'] as const;
export type WorkstationState = (typeof WORKSTATION_STATES)[number];

export type WorkstationAction = 'status' | 'open' | 'set-up' | 'signed-in';

export interface WorkstationAnswer {
  state: WorkstationState;
  /** The programs a workstation can be set up for; empty while the lever is off for this workspace. */
  hosts: string[];
  /** Each host's name as people know it ("LexPI"), the legal catalog's; a host without one is not named. */
  names: Record<string, string>;
  /** The programs this person said they signed in to there. */
  signedIn: string[];
  instanceName: string | null;
  /** Only when ready. */
  baseUrl: string | null;
  programs: Record<string, { installed: boolean; running: boolean }>;
  stage: string | null;
  canSetUp: boolean;
}

const KEY_RE = /^[a-z0-9-]{1,40}$/;

/** PURE. The callable's answer, read strictly: anything unexpected is dropped, an unknown state is null. */
export function workstationAnswerOf(data: unknown): WorkstationAnswer | null {
  const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  if (!d || d.ok !== true || !(WORKSTATION_STATES as readonly string[]).includes(String(d.state))) return null;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && KEY_RE.test(x)) : []);
  const programs: WorkstationAnswer['programs'] = {};
  if (d.programs && typeof d.programs === 'object') {
    for (const [k, v] of Object.entries(d.programs as Record<string, unknown>)) {
      if (!KEY_RE.test(k) || !v || typeof v !== 'object') continue;
      const p = v as { installed?: unknown; running?: unknown };
      programs[k] = { installed: p.installed === true, running: p.running === true };
    }
  }
  const baseUrl = typeof d.baseUrl === 'string' && /^https:\/\/(?:\d{1,3}-){3}\d{1,3}\.sslip\.io$/.test(d.baseUrl) ? d.baseUrl : null;
  const hosts = list(d.hosts);
  const names: Record<string, string> = {};
  if (d.names && typeof d.names === 'object') {
    for (const [k, v] of Object.entries(d.names as Record<string, unknown>)) {
      const name = typeof v === 'string' ? v.trim() : '';
      if (hosts.includes(k) && name && name.length <= 60 && Array.from(name).every((c) => c.charCodeAt(0) >= 0x20)) names[k] = name;
    }
  }
  return {
    state: d.state as WorkstationState,
    hosts,
    names,
    signedIn: list(d.signedIn),
    instanceName: typeof d.instanceName === 'string' && /^[a-z][-a-z0-9]{0,62}$/.test(d.instanceName) ? d.instanceName : null,
    baseUrl: d.state === 'ready' ? baseUrl : null,
    programs,
    stage: typeof d.stage === 'string' && /^[a-z0-9-]{1,60}$/.test(d.stage) ? d.stage : null,
    canSetUp: d.canSetUp === true,
  };
}

async function callPlatform(name: string, data: unknown): Promise<unknown> {
  const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([import('firebase/app'), import('firebase/functions')]);
  const res = await httpsCallable(getFunctions(getApp()), name)(data);
  return res.data;
}

/** Ask the platform. Throws on a refusal (not a member, not allowed) with the callable's own error. */
export async function askWorkstation(
  action: WorkstationAction,
  accountId: string,
  extra: { program?: string; timeZone?: string } = {},
): Promise<WorkstationAnswer> {
  const answer = workstationAnswerOf(await callPlatform(PROGRAM_WORKSTATION_CALLABLE, { action, accountId, ...extra }));
  if (!answer) throw new Error('workstation answer unreadable');
  return answer;
}

/** The person's ticket for this one machine, or null when none could be had. */
export async function ticketForWorkstation(accountId: string, instanceName: string): Promise<{ ticket: string; expiresAtMs: number } | null> {
  try {
    const data = await callPlatform(WORKSTATION_TICKET_CALLABLE, { accountId, workstation: instanceName });
    const { heldTicketOf } = await import('../browserService/workstationTickets');
    return heldTicketOf(data);
  } catch {
    return null;
  }
}

export interface ScreenPass {
  sessionId: string;
  wsPort: number;
  viewerToken: string;
  /** The program the pass asked to open, as the workstation found it. */
  program: { key: string; installed: boolean; running: boolean; launched: boolean } | null;
}

export type PassOutcome = { kind: 'pass'; pass: ScreenPass } | { kind: 'in-use' } | { kind: 'refused'; reason: string };

/** PURE. The agent's answer to a pass request. */
export function passOutcomeOf(status: number, body: unknown): PassOutcome {
  const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  if (status === 409 && b.error === 'in_use') return { kind: 'in-use' };
  if (status !== 200) return { kind: 'refused', reason: typeof b.error === 'string' ? b.error.slice(0, 60) : `http-${status}` };
  const sessionId = typeof b.sessionId === 'string' && /^desktop-[a-z0-9-]{6,64}$/i.test(b.sessionId) ? b.sessionId : '';
  const wsPort = Number(b.wsPort);
  const viewerToken = typeof b.viewerToken === 'string' && /^[A-Za-z0-9_-]{32,128}$/.test(b.viewerToken) ? b.viewerToken : '';
  if (!sessionId || !Number.isInteger(wsPort) || wsPort < 1024 || wsPort > 65535 || !viewerToken) return { kind: 'refused', reason: 'unreadable' };
  const p = b.program && typeof b.program === 'object' ? (b.program as Record<string, unknown>) : null;
  const program = p && typeof p.key === 'string' && KEY_RE.test(p.key)
    ? { key: p.key, installed: p.installed === true, running: p.running === true, launched: p.launched === true }
    : null;
  return { kind: 'pass', pass: { sessionId, wsPort, viewerToken, program } };
}

const api = (baseUrl: string, p: string) => `${baseUrl}/browser-api${p}`;
const auth = (ticket: string) => ({ Authorization: `Bearer ${ticket}` });

/** A pass onto the workstation's screen for this person, opening `program` there if it is installed. */
export async function openScreenPass(baseUrl: string, ticket: string, program: string): Promise<PassOutcome> {
  try {
    const res = await fetch(api(baseUrl, '/api/desktop'), {
      method: 'POST',
      headers: { ...auth(ticket), 'Content-Type': 'application/json' },
      body: JSON.stringify({ program }),
      signal: AbortSignal.timeout(20_000),
    });
    return passOutcomeOf(res.status, await res.json().catch(() => null));
  } catch {
    return { kind: 'refused', reason: 'unreachable' };
  }
}

/** The window is still open: the pass keeps the screen. */
export async function keepScreenPass(baseUrl: string, ticket: string, sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(api(baseUrl, `/api/desktop/${encodeURIComponent(sessionId)}/keepalive`), {
      method: 'POST',
      headers: auth(ticket),
      signal: AbortSignal.timeout(8_000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** The window closed: the screen is free for the next person at once. Never throws. */
export async function endScreenPass(baseUrl: string, ticket: string, sessionId: string): Promise<void> {
  try {
    await fetch(api(baseUrl, `/api/desktop/${encodeURIComponent(sessionId)}`), {
      method: 'DELETE',
      headers: auth(ticket),
      keepalive: true,
    });
  } catch {
    // A pass nobody ends lets go by itself two minutes after its last keepalive.
  }
}
